// --------------------------------------------------------------------------------
// Enums
// --------------------------------------------------------------------------------

/**
 * @example
 * ```graphql
 * enum Currency {
 *   CHF
 *   EUR
 *   USD
 * }
 * ```
 */
export const Currency = Object.freeze({
  CHF: 'CHF',
  EUR: 'EUR',
  USD: 'USD'
});

/**
 * @example
 * ```graphql
 * enum EntityQueryOperator {
 *   BETWEEN
 *   CONTAINS
 *   ENDS_WITH
 *   EQUAL
 *   GREATER_THAN
 *   GREATER_THAN_OR_EQUAL
 *   IN
 *   IS_NOT_NULL
 *   IS_NULL
 *   LIKE
 *   NOT_BETWEEN
 *   NOT_EQUAL
 *   NOT_IN
 *   NOT_LIKE
 *   REGEXP
 *   SMALLER_THAN
 *   SMALLER_THAN_OR_EQUAL
 *   STARTS_WITH
 * }
 * ```
 */
export const EntityQueryOperator = Object.freeze({
  BETWEEN: 'BETWEEN',
  CONTAINS: 'CONTAINS',
  ENDS_WITH: 'ENDS_WITH',
  EQUAL: 'EQUAL',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  IN: 'IN',
  IS_NOT_NULL: 'IS_NOT_NULL',
  IS_NULL: 'IS_NULL',
  LIKE: 'LIKE',
  NOT_BETWEEN: 'NOT_BETWEEN',
  NOT_EQUAL: 'NOT_EQUAL',
  NOT_IN: 'NOT_IN',
  NOT_LIKE: 'NOT_LIKE',
  REGEXP: 'REGEXP',
  SMALLER_THAN: 'SMALLER_THAN',
  SMALLER_THAN_OR_EQUAL: 'SMALLER_THAN_OR_EQUAL',
  STARTS_WITH: 'STARTS_WITH'
});

/**
 * @example
 * ```graphql
 * enum EntityType {
 *   """Product"""
 *   COMMERCE_PRODUCT
 * 
 *   """Product attribute"""
 *   COMMERCE_PRODUCT_ATTRIBUTE
 * 
 *   """Product attribute value"""
 *   COMMERCE_PRODUCT_ATTRIBUTE_VALUE
 * 
 *   """Product variation"""
 *   COMMERCE_PRODUCT_VARIATION
 * 
 *   """Language"""
 *   CONFIGURABLE_LANGUAGE
 * 
 *   """Field"""
 *   FIELD_CONFIG
 * 
 *   """Field storage"""
 *   FIELD_STORAGE_CONFIG
 * 
 *   """File"""
 *   FILE
 * 
 *   """Group"""
 *   GROUP
 * 
 *   """Media"""
 *   MEDIA
 * 
 *   """Menu"""
 *   MENU
 * 
 *   """Custom menu link"""
 *   MENU_LINK_CONTENT
 * 
 *   """Content"""
 *   NODE
 * 
 *   """Paragraph"""
 *   PARAGRAPH
 * 
 *   """Paragraphs Blokkli Edit State"""
 *   PARAGRAPHS_BLOKKLI_EDIT_STATE
 * 
 *   """Paragraphs library item"""
 *   PARAGRAPHS_LIBRARY_ITEM
 * 
 *   """Paragraphs type"""
 *   PARAGRAPHS_TYPE
 * 
 *   """Redirect"""
 *   REDIRECT
 * 
 *   """Rokka Metadata"""
 *   ROKKA_METADATA
 * 
 *   """Taxonomy term"""
 *   TAXONOMY_TERM
 * 
 *   """Translatable config pages"""
 *   TRANSLATABLE_CONFIG_PAGES
 * 
 *   """User"""
 *   USER
 * }
 * ```
 */
export const EntityType = Object.freeze({
  /** Product */
  COMMERCE_PRODUCT: 'COMMERCE_PRODUCT',
  /** Product attribute */
  COMMERCE_PRODUCT_ATTRIBUTE: 'COMMERCE_PRODUCT_ATTRIBUTE',
  /** Product attribute value */
  COMMERCE_PRODUCT_ATTRIBUTE_VALUE: 'COMMERCE_PRODUCT_ATTRIBUTE_VALUE',
  /** Product variation */
  COMMERCE_PRODUCT_VARIATION: 'COMMERCE_PRODUCT_VARIATION',
  /** Language */
  CONFIGURABLE_LANGUAGE: 'CONFIGURABLE_LANGUAGE',
  /** Field */
  FIELD_CONFIG: 'FIELD_CONFIG',
  /** Field storage */
  FIELD_STORAGE_CONFIG: 'FIELD_STORAGE_CONFIG',
  /** File */
  FILE: 'FILE',
  /** Group */
  GROUP: 'GROUP',
  /** Media */
  MEDIA: 'MEDIA',
  /** Menu */
  MENU: 'MENU',
  /** Custom menu link */
  MENU_LINK_CONTENT: 'MENU_LINK_CONTENT',
  /** Content */
  NODE: 'NODE',
  /** Paragraph */
  PARAGRAPH: 'PARAGRAPH',
  /** Paragraphs Blokkli Edit State */
  PARAGRAPHS_BLOKKLI_EDIT_STATE: 'PARAGRAPHS_BLOKKLI_EDIT_STATE',
  /** Paragraphs library item */
  PARAGRAPHS_LIBRARY_ITEM: 'PARAGRAPHS_LIBRARY_ITEM',
  /** Paragraphs type */
  PARAGRAPHS_TYPE: 'PARAGRAPHS_TYPE',
  /** Redirect */
  REDIRECT: 'REDIRECT',
  /** Rokka Metadata */
  ROKKA_METADATA: 'ROKKA_METADATA',
  /** Taxonomy term */
  TAXONOMY_TERM: 'TAXONOMY_TERM',
  /** Translatable config pages */
  TRANSLATABLE_CONFIG_PAGES: 'TRANSLATABLE_CONFIG_PAGES',
  /** User */
  USER: 'USER'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliEditableFieldConfigType {
 *   frame
 *   plain
 * }
 * ```
 */
export const ParagraphsBlokkliEditableFieldConfigType = Object.freeze({
  frame: 'frame',
  plain: 'plain'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliRemoteVideoProvider {
 *   VIMEO
 *   YOUTUBE
 * }
 * ```
 */
export const ParagraphsBlokkliRemoteVideoProvider = Object.freeze({
  VIMEO: 'VIMEO',
  YOUTUBE: 'YOUTUBE'
});

/**
 * @example
 * ```graphql
 * enum ShopifyCountry {
 *   CA
 *   UK
 *   US
 * }
 * ```
 */
export const ShopifyCountry = Object.freeze({
  CA: 'CA',
  UK: 'UK',
  US: 'US'
});